import { useEffect, useState } from "react";
import {
  collection,
  getCountFromServer,
  query,
  QueryConstraint,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

interface SubjectOption {
  label: string;
  value: string;
}

interface SubjectTableProps {
  baseConstraints: QueryConstraint[];
  subjects: readonly SubjectOption[];
  subjectField: "tuition.subject" | "SpmTuition.subject";
  enabled?: boolean;
}

const emptyCounts = (subjects: readonly SubjectOption[]) =>
  Object.fromEntries(subjects.map(({ value }) => [value, 0])) as Record<
    string,
    number
  >;

export default function SubjectTable({
  baseConstraints,
  subjects,
  subjectField,
  enabled = true,
}: SubjectTableProps) {
  const [counts, setCounts] = useState<Record<string, number>>(() =>
    emptyCounts(subjects),
  );
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const fetchCounts = async () => {
      setIsLoading(true);

      try {
        const [totalResult, ...subjectResults] = await Promise.all([
          getCountFromServer(
            query(collection(db, "leads"), ...baseConstraints),
          ),
          ...subjects.map(({ value }) =>
            getCountFromServer(
              query(
                collection(db, "leads"),
                ...baseConstraints,
                where(subjectField, "array-contains", value),
              ),
            ),
          ),
        ]);

        if (!cancelled) {
          setTotalParticipants(totalResult.data().count);
          setCounts(
            Object.fromEntries(
              subjects.map(({ value }, index) => [
                value,
                subjectResults[index].data().count,
              ]),
            ),
          );
        }
      } catch (error) {
        console.error("Failed to fetch subject counts:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void fetchCounts();

    return () => {
      cancelled = true;
    };
  }, [baseConstraints, enabled, subjectField, subjects]);

  return (
    <div className="flex flex-row flex-wrap text-xs gap-1">
      <div className="bg-blue-200 p-1 min-w-14 flex flex-col items-center justify-center rounded">
        <p className="text-center">Total Students</p>
        <p className="text-base font-semibold">
          {isLoading ? "..." : totalParticipants}
        </p>
      </div>
      {subjects.map(({ label, value }) => (
        <div
          key={value}
          className="bg-green-200 p-1 min-w-14 flex flex-col items-center justify-center rounded"
        >
          <p className="text-center">{label}</p>
          <p className="text-base font-semibold">
            {isLoading ? "..." : counts[value]}
          </p>
        </div>
      ))}
    </div>
  );
}
